import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CityCard from "../components/CityCard";
import { fetchCities } from "../lib/api";
import { useT } from "../i18n/I18nProvider";
import { AlertCircle } from "lucide-react";
import { formatMoneyFr } from "../lib/format";
import { NumbeoAttribution } from "../components/NumbeoSections";

// Numbeo nomenclature: 1 chambre / 3 chambres × centre / hors centre
const TYPES = [
  { v: "",      labelKey: "housing.allTypes",  rentKeys: ["rent_1br_center", "rent_1br_outside", "rent_3br_center", "rent_3br_outside"] },
  { v: "1br",   labelKey: "housing.type1br",   rentKeys: ["rent_1br_center", "rent_1br_outside"] },
  { v: "3br",   labelKey: "housing.type3br",   rentKeys: ["rent_3br_center", "rent_3br_outside"] },
];
const ZONES = [
  { v: "",        labelKey: "housing.fullCity",    zone: null },
  { v: "center",  labelKey: "housing.zoneCenter",  zone: "center" },
  { v: "outside", labelKey: "housing.zoneOutside", zone: "outside" },
];

const RENT_DEFINITIONS = [
  { rentKey: "rent_1br_center",  buyKey: "buy_price_m2_center",  typeKey: "1br", zoneKey: "center"  },
  { rentKey: "rent_1br_outside", buyKey: "buy_price_m2_outside", typeKey: "1br", zoneKey: "outside" },
  { rentKey: "rent_3br_center",  buyKey: "buy_price_m2_center",  typeKey: "3br", zoneKey: "center"  },
  { rentKey: "rent_3br_outside", buyKey: "buy_price_m2_outside", typeKey: "3br", zoneKey: "outside" },
];

function findByKey(list, key) {
  return (list || []).find((it) => it.key === key);
}

function typeLabel(t, typeKey) {
  return typeKey === "1br" ? t("housing.type1br") : t("housing.type3br");
}
function zoneLabel(t, zoneKey) {
  return zoneKey === "center" ? t("housing.zoneCenter") : t("housing.zoneOutside");
}

/**
 * Build a flat list of housing rows from the 7 Polish cities.
 * Each city contributes up to 4 rows (1br/3br × center/outside).
 */
function buildRows(cities) {
  const rows = [];
  for (const city of cities) {
    const inds = city?.numbeo?.indicators;
    if (!inds) continue;
    const loyers = inds.loyers || [];
    const achat = inds.achat_immobilier || [];
    for (const def of RENT_DEFINITIONS) {
      const rentInd = findByKey(loyers, def.rentKey);
      const buyInd  = findByKey(achat, def.buyKey);
      if (!rentInd) continue;
      rows.push({
        city_slug: city.slug,
        city_name: city.name_fr,
        type: def.typeKey,
        zone: def.zoneKey,
        rent_min: rentInd.min,
        rent_avg: rentInd.avg,
        rent_max: rentInd.max,
        buy_per_m2: buyInd?.avg ?? null,
        numbeo_last_update: city.numbeo.meta?.numbeo_last_update,
      });
    }
  }
  return rows;
}

export default function Housing() {
  const { t, lp } = useT();
  const [cities, setCities] = useState([]);
  const [filter, setFilter] = useState({ city: "", type: "", zone: "" });

  useEffect(() => {
    fetchCities().then((d) => setCities(d || [])).catch((e) => console.error("[Housing] cities failed:", e?.message || e));
  }, []);

  const allRows = useMemo(() => buildRows(cities), [cities]);

  const filteredRows = useMemo(() => {
    return allRows.filter((r) => {
      if (filter.city && r.city_slug !== filter.city) return false;
      if (filter.type && r.type !== filter.type) return false;
      if (filter.zone && r.zone !== filter.zone) return false;
      return true;
    });
  }, [allRows, filter]);

  // For the table footer attribution: use the city's numbeo block if filtered to one city,
  // otherwise show a generic attribution (no specific date).
  const focusedCity = filter.city ? cities.find((c) => c.slug === filter.city) : null;
  const focusedNumbeo = focusedCity?.numbeo || (cities[0]?.numbeo ? { ...cities[0].numbeo, meta: {} } : null);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-testid="housing-page">
      <header className="max-w-3xl mb-10">
        <span className="hej-chip"><span className="w-1.5 h-1.5 rounded-full bg-[#DC143C]"></span>{t("housing.chip")}</span>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-4">
          {t("housing.title")}
        </h1>
        <p className="text-slate-600 mt-4 leading-relaxed">{t("housing.intro")}</p>
      </header>

      <h2 className="font-display text-2xl font-semibold text-slate-900 mb-5">{t("housing.overview")}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-14" data-testid="housing-overview-grid">
        {cities.map((c) => <CityCard key={c.slug} city={c} />)}
      </div>

      <h2 className="font-display text-2xl font-semibold text-slate-900 mb-5">{t("housing.filterTitle")}</h2>
      <div className="bg-slate-50 rounded-2xl p-5 mb-6 flex flex-wrap gap-3" data-testid="housing-filters">
        <select value={filter.city} onChange={(e) => setFilter({ ...filter, city: e.target.value })} className="px-4 py-2.5 rounded-full bg-white border border-slate-200 text-sm font-medium" data-testid="filter-city">
          <option value="">{t("housing.allCities")}</option>
          {cities.map((c) => <option key={c.slug} value={c.slug}>{c.name_fr}</option>)}
        </select>
        <select value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })} className="px-4 py-2.5 rounded-full bg-white border border-slate-200 text-sm font-medium" data-testid="filter-type">
          {TYPES.map((tt) => <option key={tt.v} value={tt.v}>{t(tt.labelKey)}</option>)}
        </select>
        <select value={filter.zone} onChange={(e) => setFilter({ ...filter, zone: e.target.value })} className="px-4 py-2.5 rounded-full bg-white border border-slate-200 text-sm font-medium" data-testid="filter-zone">
          {ZONES.map((z) => <option key={z.v} value={z.v}>{t(z.labelKey)}</option>)}
        </select>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200" data-testid="housing-table-wrapper">
        <table className="w-full text-sm" data-testid="housing-table">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">{t("housing.tableCity")}</th>
              <th className="px-4 py-3 text-left font-semibold">{t("housing.tableType")}</th>
              <th className="px-4 py-3 text-left font-semibold">{t("housing.tableZone")}</th>
              <th className="px-4 py-3 text-right font-semibold">{t("housing.tableRentMin")}</th>
              <th className="px-4 py-3 text-right font-semibold">{t("housing.tableRentAvg")}</th>
              <th className="px-4 py-3 text-right font-semibold">{t("housing.tableRentMax")}</th>
              <th className="px-4 py-3 text-right font-semibold">{t("housing.tableBuy")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((r, i) => (
              <tr key={`${r.city_slug}-${r.type}-${r.zone}-${i}`} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`housing-row-${r.city_slug}-${r.type}-${r.zone}`}>
                <td className="px-4 py-3 font-medium text-slate-900">{r.city_name}</td>
                <td className="px-4 py-3 text-slate-600">{typeLabel(t, r.type)}</td>
                <td className="px-4 py-3 text-slate-600">{zoneLabel(t, r.zone)}</td>
                <td className="px-4 py-3 text-right text-slate-500 tabular-nums">{formatMoneyFr(r.rent_min)}</td>
                <td className="px-4 py-3 text-right font-semibold tabular-nums">{formatMoneyFr(r.rent_avg)}</td>
                <td className="px-4 py-3 text-right text-slate-500 tabular-nums">{formatMoneyFr(r.rent_max)}</td>
                <td className="px-4 py-3 text-right text-slate-500 tabular-nums">{formatMoneyFr(r.buy_per_m2)}</td>
              </tr>
            ))}
            {filteredRows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">{t("housing.noResults")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {focusedNumbeo && <NumbeoAttribution numbeo={focusedCity?.numbeo || cities[0]?.numbeo} testId="housing-attribution" />}

      <p
        className="text-slate-700 leading-relaxed mt-6"
        data-testid="housing-buy-cross-link"
      >
        Ces montants concernent l'achat au mètre carré. Pour savoir si vous pouvez acheter un bien en Pologne selon votre
        nationalité, et à quelles conditions, consultez notre guide{" "}
        <Link
          to={lp("/acheter-bien-immobilier-pologne")}
          className="text-[#0056D2] hover:underline font-medium"
          data-testid="housing-buy-cross-link-anchor"
        >
          Acheter un bien immobilier en Pologne
        </Link>
        .
      </p>

      <h2 className="font-display text-2xl font-semibold text-slate-900 mt-16 mb-5">{t("housing.tipsTitle")}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {t("housing.tips").map((tip, i) => (
          <div key={i} className="hej-card p-6" data-testid={`tip-${i}`}>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#DC143C]/10 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-4 h-4 text-[#DC143C]" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-slate-900">{tip.title}</h3>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">{tip.body}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <h2
        className="font-display text-2xl font-semibold text-slate-900 mt-16 mb-5"
        data-testid="housing-further-title"
      >
        Pour aller plus loin
      </h2>
      <Link
        to={lp("/acheter-bien-immobilier-pologne")}
        className="hej-card p-6 block group"
        data-testid="housing-buy-cta-card"
      >
        <h3 className="font-display font-semibold text-lg text-slate-900 group-hover:text-[#0056D2] transition-colors">
          Acheter un bien immobilier en Pologne
        </h3>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
          Appartement ou maison, règles selon votre nationalité, autorisation pour les pays tiers, frais et taxes : notre guide
          dédié détaille chaque cas.
        </p>
      </Link>
      <Link
        to={lp("/logement/immobilier-wroclaw")}
        className="hej-card p-6 block group mt-5"
        data-testid="housing-immobilier-wroclaw-card"
      >
        <h3 className="font-display font-semibold text-lg text-slate-900 group-hover:text-[#0056D2] transition-colors">
          Immobilier à Wrocław : quartiers et prix
        </h3>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
          Quartiers prisés, prix au m² à l'achat et à la location, rendement locatif et règles d'achat pour les ressortissants
          UE et suisses.
        </p>
      </Link>
    </div>
  );
}
